import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";

// Material-UI Components
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Tooltip from "@material-ui/core/Tooltip";

// Custom Imports
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import UserLanguageSelector from "../../components/UserLanguageSelector";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { useCookie } from "../../context/CookieContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    background: theme.palette.background.default,
  },
  
  // Seção Hero (Esquerda - Visual/Marketing)
  heroSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #171717 0%, #2d2d2d 100%)"
      : "linear-gradient(135deg, #3ecc96 0%, #2de195 100%)",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  
  bgPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "32px 32px",
    zIndex: 0,
  },
  
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#fff",
    animation: "$fadeInLeft 0.8s ease-out",
  },
  
  heroLogo: {
    width: "80%",
    maxWidth: 400,
    height: "auto",
    marginBottom: theme.spacing(4),
    filter: "brightness(0) invert(1)", // Logo branca no fundo colorido
  },
  
  heroTitle: {
    fontWeight: 700,
    fontSize: "2.5rem",
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
  },
  
  heroSubtitle: {
    fontSize: "1.25rem",
    opacity: 0.9,
    fontWeight: 400,
    maxWidth: 500,
    margin: "0 auto",
  },

  // Seção de Login (Direita - Formulário)
  loginSection: {
    width: "100%",
    maxWidth: 550,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  
  loginContainer: {
    width: "100%",
    maxWidth: 400,
    animation: "$fadeInRight 0.8s ease-out",
  },
  
  mobileLogo: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: 220,
      margin: "0 auto 32px",
    },
  },
  
  welcomeText: {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  
  formTitle: {
    fontWeight: 700,
    fontSize: "1.8rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(4),
  },
  
  form: {
    width: "100%",
  },
  
  inputField: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      }
    }
  },
  
  submitButton: {
    margin: theme.spacing(2, 0, 3),
    padding: theme.spacing(1.8),
    borderRadius: 12,
    fontWeight: 700,
    fontSize: "1rem",
    textTransform: "none",
    boxShadow: "0 4px 14px 0 rgba(62, 204, 150, 0.39)",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(62, 204, 150, 0.45)",
    },
  },
  
  linkText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },
  
  footerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  
  topActions: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    zIndex: 10,
  },
  
  actionButton: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: 12,
    boxShadow: theme.palette.type === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
    color: theme.palette.type === "dark" ? "#fff" : theme.palette.text.primary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#fff",
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },

  // Keyframes
  "@keyframes fadeInLeft": {
    "0%": { opacity: 0, transform: "translateX(-30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  },
  "@keyframes fadeInRight": {
    "0%": { opacity: 0, transform: "translateX(30px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  },
  "@keyframes fadeInUp": {
    "0%": { opacity: 0, transform: "translateY(20px)" },
    "100%": { opacity: 1, transform: "translateY(0)" }
  }
}));

const Login = () => {
  const classes = useStyles();
  const { setShowSettings } = useCookie();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { handleLogin, loading } = useContext(AuthContext);
  const [viewregister, setviewregister] = useState("disabled");

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    fetchviewregister();
  }, []);

  const fetchviewregister = async () => {
    try {
      const responsev = await api.get("/settings/viewregister");
      const viewregisterX = responsev?.data?.value;
      setviewregister(viewregisterX);
    } catch (error) {
      console.error("Error retrieving viewregister", error);
    }
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const logo = `${process.env.REACT_APP_BACKEND_URL}/public/logotipos/login.png`;
  const randomValue = Math.random();
  const logoWithRandom = `${logo}?r=${randomValue}`;

  return (
    <div className={classes.root}>
      {/* Botões de Tema e Idioma */}
      <div className={classes.topActions}>
        <IconButton 
          className={classes.actionButton}
          onClick={colorMode.toggleColorMode}
          edge="end"
        >
          {theme.palette.type === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        
        <div className={classes.actionButton} style={{ borderRadius: 12, overflow: 'hidden' }}>
          <UserLanguageSelector />
        </div>
      </div>

      {/* Lado Esquerdo - Hero Section */}
      <div className={classes.heroSection}>
        <div className={classes.bgPattern} />
        <div className={classes.heroContent}>
          <img
            src={logoWithRandom}
            alt="Logo Hero"
            className={classes.heroLogo}
          />
          <Typography className={classes.heroTitle}>
            {i18n.t("login.hero.title")}
          </Typography>
          <Typography className={classes.heroSubtitle}>
            {i18n.t("login.hero.subtitle")}
          </Typography>
        </div>
      </div>

      {/* Lado Direito - Login Form */}
      <div className={classes.loginSection}>
        <div className={classes.loginContainer}>
          <img
            src={logoWithRandom}
            alt="Logo Mobile"
            className={classes.mobileLogo}
          />
          
          <Typography className={classes.welcomeText}>
            {i18n.t("login.hero.title")}
          </Typography>
          <Typography className={classes.formTitle}>
            {i18n.t("login.formTitle")}
          </Typography>

          <form className={classes.form} onSubmit={handlSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.inputField}
              placeholder="seu@email.com"
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              className={classes.inputField}
              placeholder="••••••••"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submitButton}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                i18n.t("login.buttons.submit")
              )}
            </Button>

            <Grid container direction="column" alignItems="center" spacing={2}>
              <Grid item>
                <Link
                  component={RouterLink}
                  to="/forgetpsw"
                  className={classes.linkText}
                >
                  {i18n.t("login.links.forgotPassword")}
                </Link>
              </Grid>
              
              {viewregister === "enabled" && (
                <Grid item>
                  <Typography className={classes.footerText}>
                    {i18n.t("login.links.noAccount")}{" "}
                    <Link
                      component={RouterLink}
                      to="/signup"
                      className={classes.linkText}
                    >
                      {i18n.t("login.buttons.register")}
                    </Link>
                  </Typography>
                </Grid>
              )}
              
              <Grid item>
                <Tooltip title={i18n.t("cookieConsent.buttons.configure")}>
                  <IconButton
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className={classes.linkText}
                    size="small"
                  >
                    <img 
                      src="/cookies.png" 
                      alt="Cookie Preferences" 
                      style={{ width: 22, height: 22 }} 
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;

